import { TextField } from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import { V } from '../Layout';
import { useStyling } from '../Theme';
import Loading from './Loading';

export default function DropzoneTextArea({
    value,
    triggerUploadDialog = false,
    setOpenUploadDialog,
    onChange,
    onDropRead,
    placeholder,
    converter,
    onError,
    accept,
    setIsDataImported = undefined,
    disableTextBasedSelector,
    disableDrag = false,
    onUploadSuccess = undefined,
    onFileUploaded = undefined
}) {
    const { isDarkMode } = useStyling();
    const [isUploading, setIsUploading] = useState(false);

    const onDropRejected = (fileRejections) => {
        if (fileRejections && fileRejections.length) {
            const details = fileRejections.map((rejection) => {
                const fileName = rejection?.file?.name ?? 'unknown';
                const reason = rejection?.errors?.map((error) => error.message).join('; ');
                return reason ? `${fileName} (${reason})` : fileName;
            });

            onError(`Drop rejected for files: ${details.join(PUNCTUATION_MARKS_CONSTANTS.comma)}`);
        }
    };

    const escapeCSVValue = (value: string): string => {
        const punctuationMarks = [
            PUNCTUATION_MARKS_CONSTANTS.comma,
            PUNCTUATION_MARKS_CONSTANTS.newLine,
            PUNCTUATION_MARKS_CONSTANTS.quotes
        ];
        if (punctuationMarks.some((mark) => value.includes(mark))) {
            return `"${value.replace(/"/g, PUNCTUATION_MARKS_CONSTANTS.quotes)}"`; // escape quotes by doubling
        }
        return value;
    };

    const onDrop = (files) => {
        if (files?.length) {
            console.log(
                `Drop accepted for files: ${files.map((file) => file.name).join(PUNCTUATION_MARKS_CONSTANTS.comma)}`
            );
            const file = files[0];
            setIsUploading(true);
            let shouldNotifyUpload = false;

            converter(file)
                .then((res) => {
                    if (onUploadSuccess) {
                        shouldNotifyUpload = onUploadSuccess(res, file) !== false;
                        return;
                    }
                    if (res.data && Array.isArray(res.data.records)) {
                        const newText = res.data.records
                            .map((line) => line.data.map(escapeCSVValue).join(PUNCTUATION_MARKS_CONSTANTS.comma))
                            .join(PUNCTUATION_MARKS_CONSTANTS.newLine);
                        onDropRead(newText);
                        shouldNotifyUpload = true;
                    }
                })
                .catch((err) => {
                    console.error('File upload error:', err);
                    onError('File upload error: ' + err.message);
                })
                .finally(() => {
                    setIsUploading(false);
                    if (disableTextBasedSelector && shouldNotifyUpload) {
                        setIsDataImported(true);
                    }
                    if (shouldNotifyUpload) {
                        onFileUploaded?.(file);
                    }
                });
        }
        setOpenUploadDialog(false);
    };

    const onFileDialogCancel = () => {
        setOpenUploadDialog(false);
    };

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        onDropRejected,
        onFileDialogCancel,
        noKeyboard: true,
        noClick: true,
        noDrag: disableDrag,
        multiple: false,
        useFsAccessApi: false,
        accept
    });

    useEffect(() => {
        if (typeof open === 'function' && triggerUploadDialog) {
            open();
        }
    }, [triggerUploadDialog]);

    const targetTextArea = (
        <TextField
            disabled={isUploading}
            defaultValue={value}
            onChange={onChange}
            className={'dropzone-drop'}
            sx={{ flexGrow: 2, height: '100%', width: '100%' }}
            hiddenLabel={true}
            multiline={true}
            rows={10}
            variant={'outlined'}
            autoFocus={false}
            fullWidth={true}
            InputProps={{
                inputProps: {
                    placeholder: placeholder,
                    sx: {
                        flexGrow: 2,
                        resize: 'vertical',
                        overflowY: 'auto',
                        minHeight: '20px',
                        height: '100%'
                    }
                }
            }}
        />
    );

    return (
        <V
            sx={{
                '& .MuiOutlinedInput-root': { flexGrow: 2 },
                '& .MuiInputBase-input': { height: '100% !important' },
                backgroundColor: isDragActive ? (isDarkMode ? grey['700'] : grey['300']) : 'none',
                color: isDragActive ? (isDarkMode ? grey['500'] : grey['500']) : 'inherit',
                height: '100%',
                overflow: 'hidden',
                backgroundImage: isDragActive
                    ? 'url("https://cdn2.iconfinder.com/data/icons/ios-7-icons/50/upload-512.png")'
                    : 'none',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '64px 64px'
            }}
        >
            <div className="dropzone-container" style={{ height: '100%', width: '100%' }} {...getRootProps()}>
                <input className="dropzone-hidden" {...getInputProps()} />
                {isUploading ? <Loading /> : targetTextArea}
            </div>
        </V>
    );
}
